import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Table, Button, Modal, Form, AutoComplete, Input, Popconfirm,
  Space, Tag, message, Tooltip, Typography,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  MailOutlined, UserOutlined, ReloadOutlined, CrownOutlined,
} from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { fetchWithRetry } from '../utils/fetchWithRetry';

const { Text } = Typography;
const BASE = process.env.REACT_APP_API_URL;

interface SuperAdminRecord {
  superAdminName: string;
  superAdminEmail: string;
}

const ACESuperAdmin: React.FC = () => {
  const { instance, accounts } = useMsal();

  const [records, setRecords]         = useState<SuperAdminRecord[]>([]);
  const [loading, setLoading]         = useState(false);
  const [modalOpen, setModalOpen]     = useState(false);
  const [editing, setEditing]         = useState<SuperAdminRecord | null>(null);
  const [saving, setSaving]           = useState(false);
  const [pageSize, setPageSize]       = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [emailOptions, setEmailOptions] = useState<{ value: string; label: string }[]>([]);
  const [emailSearching, setEmailSearching] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [form] = Form.useForm();

  /* ── Auth token ── */
  const fetchToken = useCallback(async () => {
    const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return res.idToken;
  }, [instance, accounts]);

  /* ── Email autocomplete search ── */
  const handleEmailSearch = useCallback((val: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!val || val.length < 2) { setEmailOptions([]); return; }
    setEmailSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const token = await fetchToken();
        const res = await fetchWithRetry(`${BASE}/api/Users/Search?search=${encodeURIComponent(val)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) { setEmailOptions([]); return; }
        const data = await res.json();
        setEmailOptions(
          (data ?? []).map((u: any) => ({
            value: u.email ?? u.Email ?? '',
            label: `${u.displayName ?? u.DisplayName ?? ''} — ${u.email ?? u.Email ?? ''}`,
          }))
        );
      } catch {
        setEmailOptions([]);
      } finally {
        setEmailSearching(false);
      }
    }, 300);
  }, [fetchToken]);

  /* ── Load all records ── */
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const token = await fetchToken();
      const res = await fetchWithRetry(`${BASE}/api/nominations/superadmin/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      const rows: SuperAdminRecord[] = (data.responseData ?? data ?? []).map((r: any) => ({
        superAdminEmail: r.superAdminEmail ?? r.SuperAdminEmail ?? '',
        superAdminName:  r.superAdminName  ?? r.SuperAdminName  ?? '',
      }));
      rows.sort((a, b) => a.superAdminName.localeCompare(b.superAdminName));
      setRecords(rows);
    } catch {
      message.error('Failed to load Super Admin records.');
    } finally {
      setLoading(false);
    }
  }, [fetchToken]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  /* ── Open modal ── */
  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setEmailOptions([]);
    setModalOpen(true);
  };

  const openEdit = (record: SuperAdminRecord) => {
    setEditing(record);
    form.setFieldsValue({ superAdminEmail: record.superAdminEmail, superAdminName: record.superAdminName });
    setEmailOptions([]);
    setModalOpen(true);
  };

  /* ── Save (Add or Update) ── */
  const handleSave = async () => {
    let values: { superAdminEmail: string; superAdminName: string };
    try { values = await form.validateFields(); }
    catch { return; }

    setSaving(true);
    try {
      const token = await fetchToken();

      if (editing) {
        const res = await fetchWithRetry(`${BASE}/api/nominations/superadmin/update`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            oldEmail: editing.superAdminEmail,
            newEmail: values.superAdminEmail,
            newName:  editing.superAdminName,   // name is not editable, keep original
          }),
        });
        if (!res.ok) throw new Error(await res.text());
        message.success('Super Admin updated successfully.');
      } else {
        const res = await fetchWithRetry(`${BASE}/api/nominations/superadmin/add`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ superAdminEmail: values.superAdminEmail, superAdminName: values.superAdminName }),
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text.includes('already exists') ? 'This email already exists as a Super Admin.' : text);
        }
        message.success('Super Admin added successfully.');
      }

      setModalOpen(false);
      loadRecords();
    } catch (err: any) {
      message.error(err.message || 'Operation failed.');
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async (record: SuperAdminRecord) => {
    try {
      const token = await fetchToken();
      const params = new URLSearchParams({ email: record.superAdminEmail });
      const res = await fetchWithRetry(`${BASE}/api/nominations/superadmin/delete?${params}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      message.success('Super Admin deleted successfully.');
      loadRecords();
    } catch (err: any) {
      message.error(err.message || 'Delete failed.');
    }
  };

  /* ── Table columns ── */
  const columns = [
    {
      title: '#',
      width: 60,
      render: (_: any, __: any, index: number) => (
        <Text type="secondary" style={{ fontSize: 13 }}>{(currentPage - 1) * pageSize + index + 1}</Text>
      ),
    },
    {
      title: (
        <Space><UserOutlined style={{ color: '#8629A4' }} /> Name</Space>
      ),
      dataIndex: 'superAdminName',
      key: 'superAdminName',
      sorter: (a: SuperAdminRecord, b: SuperAdminRecord) => a.superAdminName.localeCompare(b.superAdminName),
      render: (val: string) => (
        <Text style={{ fontSize: 13.5 }}>{val || <Text type="secondary">—</Text>}</Text>
      ),
    },
    {
      title: (
        <Space><MailOutlined style={{ color: '#8629A4' }} /> Email</Space>
      ),
      dataIndex: 'superAdminEmail',
      key: 'superAdminEmail',
      sorter: (a: SuperAdminRecord, b: SuperAdminRecord) => a.superAdminEmail.localeCompare(b.superAdminEmail),
      render: (val: string) => (
        <Text style={{ fontSize: 13.5 }}>{val}</Text>
      ),
    },
    {
      title: 'Role',
      width: 140,
      render: () => (
        <Tag color="purple" icon={<CrownOutlined />} style={{ fontWeight: 600 }}>Super Admin</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: SuperAdminRecord) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              style={{ color: '#8629A4' }}
              onClick={() => openEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Remove Super Admin"
            description={`Remove "${record.superAdminName || record.superAdminEmail}" from Super Admins?`}
            onConfirm={() => handleDelete(record)}
            okText="Yes, Remove"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="text" icon={<DeleteOutlined />} size="small" danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* ── Toolbar ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {records.length} super admin{records.length !== 1 ? 's' : ''} configured
        </Text>
        <Space>
          <Tooltip title="Refresh">
            <Button icon={<ReloadOutlined />} onClick={loadRecords} loading={loading} />
          </Tooltip>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openAdd}
            style={{ background: '#8629A4', borderColor: '#8629A4' }}
          >
            Add Super Admin
          </Button>
        </Space>
      </div>

      {/* ── Table ── */}
      <Table
        rowKey={r => r.superAdminEmail}
        columns={columns}
        dataSource={records}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: t => `Total ${t} records`,
          onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
        }}
        bordered
        size="middle"
        style={{ borderRadius: 8, overflow: 'hidden' }}
        rowClassName={(_, index) => index % 2 === 0 ? '' : 'ant-table-row-alt'}
      />

      {/* ── Add / Edit Modal ── */}
      <Modal
        title={
          <Space>
            {editing ? <EditOutlined style={{ color: '#8629A4' }} /> : <PlusOutlined style={{ color: '#8629A4' }} />}
            <span>{editing ? 'Edit Super Admin' : 'Add Super Admin'}</span>
          </Space>
        }
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText={editing ? 'Update' : 'Add'}
        confirmLoading={saving}
        okButtonProps={{ style: { background: '#8629A4', borderColor: '#8629A4' } }}
        destroyOnClose
        width={480}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="superAdminEmail"
            label="Email Address"
            rules={[
              { required: true, message: 'Email is required.' },
              { type: 'email', message: 'Enter a valid email address.' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const isDuplicate = records.some(
                    r => r.superAdminEmail.toLowerCase() === value.toLowerCase() &&
                         r.superAdminEmail.toLowerCase() !== editing?.superAdminEmail.toLowerCase()
                  );
                  return isDuplicate
                    ? Promise.reject('This email already exists as a Super Admin.')
                    : Promise.resolve();
                },
              },
            ]}
          >
            <AutoComplete
              options={emailOptions}
              onSearch={handleEmailSearch}
              onSelect={(val: string) => {
                const matched = emailOptions.find(o => o.value === val);
                const name = matched?.label.split(' — ')[0] ?? '';
                form.setFieldsValue({ superAdminEmail: val, superAdminName: name });
              }}
              filterOption={false}
              notFoundContent={emailSearching ? 'Searching…' : null}
              allowClear
            >
              <Input
                prefix={<MailOutlined style={{ color: '#bbb' }} />}
                placeholder="Type name or email to search…"
              />
            </AutoComplete>
          </Form.Item>

          <Form.Item
            name="superAdminName"
            label="Display Name"
          >
            <Input
              prefix={<UserOutlined style={{ color: '#bbb' }} />}
              disabled
              style={{ background: 'rgba(134,41,164,0.04)', color: '#555' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ACESuperAdmin;
